import { Result } from '@/generic/functional';
import { RuleId } from '@/rule';
import { Inclusion } from '@/diagnostic/domain/Inclusion.ts';
import type { RulesSelection } from '@/diagnostic/domain/RulesSelection.ts';

type SelectedRuleIdsBuilder = {
  inclusion: Inclusion;
  ruleIds: RuleId[];
  count: number;
};

export class SelectedRuleIds {
  readonly #inclusion: Inclusion;
  readonly #ruleIds: RuleId[];
  readonly #count: number;

  private constructor(builder: SelectedRuleIdsBuilder) {
    this.#inclusion = builder.inclusion;
    this.#ruleIds = builder.ruleIds;
    this.#count = builder.count;
  }

  static of(...rules: (RuleId | string)[]): Result<Error, SelectedRuleIds> {
    if (rules.length === 0) {
      return Result.error(new Error('Rules should have at least one rule'));
    }
    const ruleIds = rules.map((rule) => RuleId.of(rule));
    return Result.success(
      new SelectedRuleIds({ inclusion: Inclusion.Include, ruleIds, count: ruleIds.length }),
    );
  }

  static fromSelection(selection: RulesSelection): Result<Error, SelectedRuleIds> {
    if (selection.isNoneSelected) {
      return Result.error(new Error('Rules should have at least one rule'));
    }
    return Result.success(
      new SelectedRuleIds({
        inclusion: selection.inclusion,
        ruleIds: selection.exceptions,
        count: selection.count,
      }),
    );
  }

  get inclusion(): Inclusion {
    return this.#inclusion;
  }

  get list(): RuleId[] {
    return this.#ruleIds;
  }

  get count(): number {
    return this.#count;
  }
}
